import classNames from 'classnames';
import { type FC, type MouseEvent, memo, useEffect } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';

import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { Button } from 'components/Button/Button';
import { ButtonAnchor } from 'components/Button/ButtonAnchor';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import ScrollingTabs from 'components/ScrollingTabs';
import { useLocale } from 'containers/App/LocaleContext';
import { usePrevious } from 'utils/hooks/usePrevious';
import type {
  DetachedDeviceViewEntities,
  DeviceViewElementType,
  DeviceViewEntity,
  DeviceViewEntityId,
} from './deviceViewTypes';
import { getTitle } from './helpers/getTitle';
import { useDeviceViewContextSelectors } from './model/DeviceViewModel';
import { detachDeviceViewEntity } from './model/commands/detachDeviceViewEntity';
import { selectDeviceViewEntity } from './model/commands/selectDeviceViewEntity';

export interface ActiveDeviceViewEntityChangeEvent {
  deviceViewElement: DeviceViewElementType;
  deviceViewEntityId: DeviceViewEntityId;
}

export interface DeviceViewEntityTabMouseEvent {
  deviceViewElement: DeviceViewElementType;
  deviceViewEntityId: DeviceViewEntityId;
}

interface Props {
  activeDeviceViewEntityId: DeviceViewEntityId;
  detachedDeviceViewEntities: DetachedDeviceViewEntities;
  disableDetach: boolean | undefined;
  isDragging: boolean;
  onClose: (deviceViewEntityId?: DeviceViewEntityId) => void;
}

interface TabItemProps {
  deviceViewEntity: DeviceViewEntity;
  disableDetach: boolean | undefined;
  onClose: (deviceViewEntityId: DeviceViewEntityId) => void;
}

const TabItem: FC<TabItemProps> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    dispatch: prop('dispatch'),
    handlersContainer: prop('handlersContainer'),
  });
  const msg = useLocale();

  const handleTabItemClick = useStableCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation();
      e.preventDefault();
      ctxProps.dispatch(selectDeviceViewEntity, {
        deviceViewEntityId: props.deviceViewEntity.id,
      });
      ctxProps.handlersContainer.handleActiveDeviceEntityChange?.({
        deviceViewEntityId: props.deviceViewEntity.id,
        deviceViewElement: props.deviceViewEntity.deviceViewElement,
      });
    },
  );

  const handlePinIconClick = useStableCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    ctxProps.dispatch(detachDeviceViewEntity, {
      deviceViewEntityId: props.deviceViewEntity.id,
    });
    ctxProps.handlersContainer.handleActiveDeviceEntityChange?.({
      deviceViewEntityId: props.deviceViewEntity.id,
      deviceViewElement: props.deviceViewEntity.deviceViewElement,
    });
  });

  const handleCloseIconClick = useStableCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();

      // If mouse cursor ends up on another tab after closing mouseleave is not triggered
      // so we need to call it manually to unhighlight edge
      handleTabMouseLeave();

      props.onClose(props.deviceViewEntity.id);
    },
  );

  const handleTabMouseEnter = () => {
    ctxProps.handlersContainer.handleDeviceViewEntityTabMouseEnter?.({
      deviceViewEntityId: props.deviceViewEntity.id,
      deviceViewElement: props.deviceViewEntity.deviceViewElement,
    });
  };

  const handleTabMouseLeave = () => {
    ctxProps.handlersContainer.handleDeviceViewEntityTabMouseLeave?.({
      deviceViewEntityId: props.deviceViewEntity.id,
      deviceViewElement: props.deviceViewEntity.deviceViewElement,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: just cleanup on unmount
  useEffect(() => {
    return () => {
      ctxProps.handlersContainer.handleDeviceViewEntityTabMouseLeave?.({
        deviceViewEntityId: props.deviceViewEntity.id,
        deviceViewElement: props.deviceViewEntity.deviceViewElement,
      });
    };
  }, []);

  return (
    <ButtonAsA
      onClick={handleTabItemClick}
      onMouseEnter={handleTabMouseEnter}
      onMouseLeave={handleTabMouseLeave}
    >
      <span className='mr-4 inline-block align-middle'>
        {getTitle(msg, props.deviceViewEntity)}
      </span>
      {!props.disableDetach && (
        <Tooltip content={msg.deviceView.showInWindow}>
          <Button
            className='ml-4 mr-1'
            onClick={handlePinIconClick}
            variant='tertiary'
            icon='fa-external-link-alt'
            size='xxxs'
            type='button'
          />
        </Tooltip>
      )}
      <Tooltip content={msg.deviceView.closeTab}>
        <Button
          variant='tertiary'
          icon='fa-xmark'
          onClick={handleCloseIconClick}
          size='xxxs'
          type='button'
        />
      </Tooltip>
    </ButtonAsA>
  );
};

const getStartIndex = (
  previousDeviceViewEntitiesLength: number,
  currentDeviceViewEntitiesLength: number,
): number | undefined => {
  if (previousDeviceViewEntitiesLength < currentDeviceViewEntitiesLength) {
    return 0;
  }
  return undefined;
};

const BareDeviceViewHeader: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    deviceViewEntities: prop('deviceViewEntities'),
  });
  const previousDeviceViewEntitiesLength =
    usePrevious(ctxProps.deviceViewEntities.length) ?? 0;
  const startIndex = getStartIndex(
    previousDeviceViewEntitiesLength,
    ctxProps.deviceViewEntities.length,
  );

  const handleCloseAllEntitiesButtonClick = useStableCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      props.onClose();
    },
  );

  return (
    <div className='device-view__header scrolling-tabs pt-lgu-2 flex-1'>
      <div className='nav-tabs-custom mb-0 flex h-full'>
        <ScrollingTabs
          activeTabId={props.activeDeviceViewEntityId}
          scContainerClassName='flex-1 h-full'
          parentSelector='.device-view__header'
          startIndex={startIndex}
        >
          {ctxProps.deviceViewEntities
            .filter(
              (deviceViewEntity) =>
                !props.detachedDeviceViewEntities.includes(deviceViewEntity.id),
            )
            .map((deviceViewEntity) => {
              return (
                <li
                  key={deviceViewEntity.id}
                  className={classNames(
                    props.activeDeviceViewEntityId === deviceViewEntity.id &&
                      'active',
                    props.isDragging
                      ? 'pointer-events-none'
                      : 'pointer-events-auto',
                  )}
                >
                  <TabItem
                    deviceViewEntity={deviceViewEntity}
                    disableDetach={props.disableDetach}
                    onClose={props.onClose}
                  />
                </li>
              );
            })}
        </ScrollingTabs>
        {ctxProps.deviceViewEntities.length > 1 && (
          <div className='nav nav-tabs pointer-events-auto'>
            <li className='device-view__header-close'>
              <ButtonAnchor
                variant='tertiary'
                size='xxxs'
                icon='fa-xmark'
                href='#'
                onClick={handleCloseAllEntitiesButtonClick}
                className='size-full'
                type='button'
              />
            </li>
          </div>
        )}
      </div>
    </div>
  );
};

export const DeviceViewHeader = memo(BareDeviceViewHeader);
